<template>
  <div class="container p-4">
    <h3 class="text-xl font-bold mb-3">Capítulos</h3>

    <CardPrime class="mb-4">
      <template #title>Filtros (obligatorios)</template>
      <template #content>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label class="block text-sm mb-1">Curso</label>
            <DropdownPrime
              v-model="selectedCursoId"
              :options="cursos"
              optionLabel="Glosa"
              optionValue="Id"
              placeholder="Selecciona curso"
              class="w-full"
              showClear
            />
          </div>
          <div>
            <label class="block text-sm mb-1">Asignatura</label>
            <DropdownPrime
              v-model="selectedAsignaturaId"
              :options="asignaturas"
              optionLabel="Glosa"
              optionValue="Id"
              placeholder="Selecciona asignatura"
              class="w-full"
              showClear
            />
          </div>
          <div>
            <label class="block text-sm mb-1">Glosa (opcional)</label>
            <InputText v-model="filtroGlosa" placeholder="Contiene..." class="w-full" />
          </div>
          <div class="flex gap-2">
            <ButtonPrime label="Aplicar" icon="pi pi-filter" @click="aplicarFiltros" />
            <ButtonPrime
              label="Nuevo capítulo"
              icon="pi pi-plus"
              class="p-button-success"
              :disabled="!puedeCargar"
              @click="abrirNuevo"
            />
          </div>
        </div>
        <p v-if="!puedeCargar" class="text-sm text-gray-600 mt-2">Selecciona curso y asignatura para ver o crear capítulos.</p>
      </template>
    </CardPrime>

    <DataTablePrime
      :value="capitulos"
      :loading="loading"
      class="p-datatable-sm"
      responsiveLayout="scroll"
      :emptyMessage="puedeCargar ? 'Sin resultados' : 'Aplica filtros para cargar datos'"
    >
      <ColumnPrime field="Id" header="ID" />
      <ColumnPrime field="Glosa" header="Glosa" />
      <ColumnPrime field="Orden" header="Orden" />
      <ColumnPrime header="Vigente">
        <template #body="{ data }">{{ data.Vigente ? 'Sí' : 'No' }}</template>
      </ColumnPrime>
      <ColumnPrime header="Acciones">
        <template #body="{ data }">
          <ButtonPrime icon="pi pi-pencil" class="p-button-sm p-mr-1" @click="abrirEditar(data)" />
          <ButtonPrime
            v-if="data.Vigente"
            label="Desactivar"
            class="p-button-sm p-button-warning"
            @click="desactivar(data)"
          />
        </template>
      </ColumnPrime>
    </DataTablePrime>

    <DialogPrime v-model:visible="dialogVisible" :modal="true" :header="dialogTitulo" :style="{ width: '520px' }">
      <div class="mb-3 p-3 border-round surface-ground border-1 surface-border">
        <div class="text-sm font-semibold mb-2 text-700">Este capítulo se guardará para:</div>
        <div class="text-sm mb-1">
          <span class="font-medium text-900">Curso:</span>
          {{ modalCursoDescripcion }}
        </div>
        <div class="text-sm">
          <span class="font-medium text-900">Asignatura:</span>
          {{ modalAsignaturaDescripcion }}
        </div>
      </div>
      <div class="field mb-3">
        <label class="block mb-1">Glosa</label>
        <InputText v-model="form.Glosa" class="w-full" />
      </div>
      <div class="grid grid-cols-2 gap-3">
        <div class="field mb-3">
          <label class="block mb-1">Orden</label>
          <InputNumber v-model="form.Orden" :min="1" class="w-full" />
        </div>
        <div class="field mb-3 flex align-items-end pb-2">
          <CheckboxPrime v-model="form.Vigente" :binary="true" inputId="cap-vig" />
          <label for="cap-vig" class="ml-2">Vigente</label>
        </div>
      </div>
      <template #footer>
        <ButtonPrime label="Cancelar" class="p-button-secondary" @click="dialogVisible = false" />
        <ButtonPrime label="Guardar" @click="guardar" />
      </template>
    </DialogPrime>
  </div>
</template>

<script>
import CardPrime from 'primevue/card';
import DataTablePrime from 'primevue/datatable';
import ColumnPrime from 'primevue/column';
import DialogPrime from 'primevue/dialog';
import ButtonPrime from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import DropdownPrime from 'primevue/dropdown';
import CheckboxPrime from 'primevue/checkbox';
import CursoService from '@/services/CursoService';
import AsignaturaService from '@/services/AsignaturaService';
import CapituloUnidadService from '@/services/CapituloUnidadService';

export default {
  name: 'MantenedorCapitulos',
  components: {
    CardPrime,
    DataTablePrime,
    ColumnPrime,
    DialogPrime,
    ButtonPrime,
    InputText,
    InputNumber,
    DropdownPrime,
    CheckboxPrime,
  },
  data() {
    return {
      cursos: [],
      asignaturas: [],
      selectedCursoId: null,
      selectedAsignaturaId: null,
      filtroGlosa: '',
      capitulos: [],
      loading: false,
      dialogVisible: false,
      dialogTitulo: 'Capítulo',
      modalCursoDescripcion: '',
      modalAsignaturaDescripcion: '',
      form: {
        Id: 0,
        Glosa: '',
        Vigente: true,
        Orden: 1,
      },
    };
  },
  computed: {
    puedeCargar() {
      return this.selectedCursoId != null && this.selectedAsignaturaId != null;
    },
  },
  methods: {
    parseContent(response) {
      const payload = response?.data || {};
      return payload.Content || payload.content || [];
    },
    async cargarCombos() {
      const [c, a] = await Promise.all([CursoService.get(), AsignaturaService.get()]);
      this.cursos = c.data || [];
      this.asignaturas = a.data || [];
    },
    aplicarFiltros() {
      if (!this.puedeCargar) {
        alert('Selecciona curso y asignatura.');
        return;
      }
      this.cargarLista();
    },
    async cargarLista() {
      this.loading = true;
      try {
        const resp = await CapituloUnidadService.listarCapitulos(1, 500, {
          glosa: this.filtroGlosa?.trim() || null,
          idCurso: this.selectedCursoId,
          idAsignatura: this.selectedAsignaturaId,
        });
        this.capitulos = this.parseContent(resp);
      } finally {
        this.loading = false;
      }
    },
    descripcionCursoPorId(id) {
      const c = this.cursos.find((x) => x.Id === id);
      return c?.Glosa ? `${c.Glosa} (id ${id})` : `id ${id}`;
    },
    descripcionAsignaturaPorId(id) {
      const a = this.asignaturas.find((x) => x.Id === id);
      return a?.Glosa ? `${a.Glosa} (id ${id})` : `id ${id}`;
    },
    rellenarContextoModalDesdeFiltros() {
      this.modalCursoDescripcion = this.descripcionCursoPorId(this.selectedCursoId);
      this.modalAsignaturaDescripcion = this.descripcionAsignaturaPorId(this.selectedAsignaturaId);
    },
    rellenarContextoModalDesdeFila(row) {
      const idC = row.Curso?.Id ?? this.selectedCursoId;
      const idA = row.Asignatura?.Id ?? this.selectedAsignaturaId;
      this.modalCursoDescripcion = row.Curso?.Glosa
        ? `${row.Curso.Glosa} (id ${idC})`
        : this.descripcionCursoPorId(idC);
      this.modalAsignaturaDescripcion = row.Asignatura?.Glosa
        ? `${row.Asignatura.Glosa} (id ${idA})`
        : this.descripcionAsignaturaPorId(idA);
    },
    abrirNuevo() {
      if (!this.puedeCargar) return;
      this.dialogTitulo = 'Nuevo capítulo';
      this.rellenarContextoModalDesdeFiltros();
      this.form = { Id: 0, Glosa: '', Vigente: true, Orden: 1 };
      this.dialogVisible = true;
    },
    abrirEditar(row) {
      this.dialogTitulo = 'Editar capítulo';
      this.rellenarContextoModalDesdeFila(row);
      this.form = {
        Id: row.Id,
        Glosa: row.Glosa,
        Vigente: !!row.Vigente,
        Orden: row.Orden || 1,
      };
      this.dialogVisible = true;
    },
    payloadDesdeForm() {
      return {
        Id: this.form.Id || 0,
        Glosa: (this.form.Glosa || '').trim(),
        Vigente: !!this.form.Vigente,
        Orden: Number(this.form.Orden || 1),
        Asignatura: {
          Id: Number(this.selectedAsignaturaId),
          Glosa: null,
          Pais: null,
        },
        Curso: {
          Id: Number(this.selectedCursoId),
          Glosa: null,
          Vigente: false,
          Orden: 0,
          Pais: null,
        },
      };
    },
    async guardar() {
      const p = this.payloadDesdeForm();
      if (!p.Glosa) {
        alert('Ingresa la glosa.');
        return;
      }
      try {
        if (p.Id > 0) {
          await CapituloUnidadService.actualizarCapitulo(p.Id, p);
        } else {
          await CapituloUnidadService.guardarCapitulo(p);
        }
        this.dialogVisible = false;
        await this.cargarLista();
      } catch (e) {
        alert(e.response?.data?.Message || e.message || 'Error al guardar');
      }
    },
    async desactivar(row) {
      if (!confirm(`¿Desactivar el capítulo ${row.Id}?`)) return;
      const p = {
        Id: row.Id,
        Glosa: row.Glosa,
        Vigente: false,
        Orden: row.Orden || 1,
        Asignatura: {
          Id: row.Asignatura?.Id ?? this.selectedAsignaturaId,
          Glosa: null,
          Pais: null,
        },
        Curso: {
          Id: row.Curso?.Id ?? this.selectedCursoId,
          Glosa: null,
          Vigente: false,
          Orden: 0,
          Pais: null,
        },
      };
      try {
        await CapituloUnidadService.actualizarCapitulo(row.Id, p);
        await this.cargarLista();
      } catch (e) {
        alert(e.response?.data?.Message || e.message || 'Error');
      }
    },
  },
  async mounted() {
    await this.cargarCombos();
  },
};
</script>
